import { CONTENT_BASE_PATH } from '../constants.js';
import {
	escapeHtml,
	slugify,
	extractTitleFromMarkdown,
	formatTitleFromSlug
} from '../helpers.js';

/**
 * Attach lore/content loading helpers to MapApp.
 * @param {typeof import('../app.js').default} App
 */
export function applyContentMixins(App) {
	/**
	 * Build the markdown path for a given entity type/id.
	 * @param {'city'|'nation'} kind
	 * @param {string} infoId
	 * @returns {string}
	 */
	App.prototype.getMarkdownPath = function getMarkdownPath(kind, infoId) {
		const folder = kind === 'city' ? 'cities' : 'nations';
		const basePath = CONTENT_BASE_PATH.replace(/\/+$/, '');
		return `${basePath}/${folder}/${infoId}.md`;
	};

	/**
	 * Resolve the canonical info slug for an entity.
	 * @param {object} entity
	 * @returns {string|null}
	 */
	App.prototype.getInfoId = function getInfoId(entity) {
		if (!entity || !entity.name) {
			return entity && entity.slug ? entity.slug : null;
		}
		if (entity.infoId) {
			return entity.infoId;
		}
		if (entity.slug) {
			return entity.slug;
		}
		return slugify(entity.name);
	};

	/**
	 * Parse internal lore links and return metadata about the target.
	 * @param {string} href
	 * @returns {{kind:'city'|'nation'|'content',slug:string,path:string}|null}
	 */
	App.prototype.resolveContentLink = function resolveContentLink(href) {
		const sanitized = (href || '').trim();
		if (!sanitized || sanitized.charAt(0) === '#') {
			return null;
		}
		if (/^(mailto|tel|javascript):/i.test(sanitized)) {
			return null;
		}

		const contentBasePathRaw = CONTENT_BASE_PATH.replace(/\/+$/, '');

		let path;
		try {
			const url = new URL(sanitized, globalThis.location.href);
			const contentBaseUrl = new URL(`${contentBasePathRaw}/`, globalThis.location.origin);
			if (url.origin !== globalThis.location.origin && url.origin !== contentBaseUrl.origin) {
				return null;
			}
			path = url.pathname;
		} catch (_error) {
			return null;
		}

		path = path.replace(/^\/+/, '');

		if (!path) {
			return null;
		}

		const normalizeSlashes = (value) => value.replace(/^\/+|\/+$/g, '');
		const contentBasePath = normalizeSlashes(contentBasePathRaw);
		const normalizedPath = normalizeSlashes(path);
		let relativePath = normalizedPath;

		if (contentBasePath && normalizedPath.startsWith(`${contentBasePath}/`)) {
			relativePath = normalizedPath.slice(contentBasePath.length + 1);
		} else {
			const contentIndex = normalizedPath.indexOf('content/');
			if (contentIndex !== -1) {
				relativePath = normalizedPath.slice(contentIndex + 'content/'.length);
			}
		}
		const segments = relativePath.split('/');
		const folder = segments.shift();
		if (!folder) {
			return null;
		}

		const filename = segments.join('/');
		if (!filename || filename.indexOf('.') === -1) {
			return null;
		}

		if (!/\.md$/i.test(filename)) {
			return null;
		}

		const slug = filename.replace(/\.md$/i, '');
		const kind = folder === 'cities' ? 'city' : folder === 'nations' ? 'nation' : 'content';
		return {
			kind,
			slug,
			path: `${contentBasePathRaw}/${folder}/${slug}.md`
		};
	};

	/**
	 * Follow a lore link by selecting the entity or loading raw markdown.
	 * @param {{kind:'city'|'nation'|'content',slug:string,path:string}} link
	 */
	App.prototype.handleContentLink = function handleContentLink(link) {
		if (!link) {
			return;
		}

		if (link.kind === 'city' || link.kind === 'nation') {
			const entity = this.findEntityBySlug(link.kind, link.slug);
			if (entity) {
				this.showEntityInfo(link.kind, entity);
				return;
			}
		}

		this.loadMarkdownByPath(link.kind, link.slug, link.path);
		this.clearHighlight();
	};

	/**
	 * Find a city/nation by slug from the global data arrays.
	 * @param {'city'|'nation'} kind
	 * @param {string} slug
	 * @returns {object|null}
	 */
	App.prototype.findEntityBySlug = function findEntityBySlug(kind, slug) {
		if (kind !== 'city' && kind !== 'nation') {
			return null;
		}
		const list = kind === 'city' ? globalThis.cities : globalThis.countries;
		if (!Array.isArray(list)) {
			return null;
		}
		for (let i = 0; i < list.length; i += 1) {
			if (this.getInfoId(list[i]) === slug) {
				return list[i];
			}
		}
		return null;
	};

	/**
	 * Fetch and render markdown for a given path and update the panel.
	 * @param {'city'|'nation'} kind
	 * @param {string} slug
	 * @param {string} markdownPath
	 */
	App.prototype.loadMarkdownByPath = function loadMarkdownByPath(
		kind,
		slug,
		markdownPath
	) {
		const fallbackTitle = formatTitleFromSlug(slug);
		this.updateAnchor(kind, slug);
		this.setPanelMessage(
			fallbackTitle,
			'<p class="info-panel__placeholder">Loading lore...</p>'
		);

		const cached = this.infoCache[markdownPath];
		if (cached) {
			const cachedHtml = typeof cached === 'string' ? cached : cached.html;
			const cachedTitle =
				typeof cached === 'object' && cached.title ? cached.title : fallbackTitle;
			this.setPanelContent(cachedTitle, cachedHtml);
			return;
		}

		fetch(markdownPath)
			.then((response) => {
				if (!response.ok) {
					throw new Error(`Lore not found: ${markdownPath}`);
				}
				return response.text();
			})
			.then((markdown) => {
				const derivedTitle = extractTitleFromMarkdown(markdown) || fallbackTitle;
				const html = this.renderMarkdown(markdown);
				this.infoCache[markdownPath] = {
					html,
					title: derivedTitle
				};
				this.setPanelContent(derivedTitle, html);
			})
			.catch((error) => {
				console.warn(error);
				this.setPanelMessage(
					fallbackTitle,
					'<p class="info-panel__placeholder">Lore has not been uploaded for this location yet.</p>'
				);
			});
	};

	/**
	 * Lazily load the `marked` markdown renderer if it is not yet present.
	 * @returns {Promise<typeof marked>}
	 */
	App.prototype.ensureMarkdownRenderer = function ensureMarkdownRenderer() {
		if (globalThis.marked && typeof globalThis.marked.parse === 'function') {
			return Promise.resolve(globalThis.marked);
		}
		if (this.markedLoader) {
			return this.markedLoader;
		}
		this.markedLoader = new Promise((resolve, reject) => {
			const script = document.createElement('script');
			script.src = 'vendor/marked/marked.min.js';
			script.async = true;
			script.onload = () => {
				if (globalThis.marked && typeof globalThis.marked.parse === 'function') {
					resolve(globalThis.marked);
				} else {
					reject(new Error('Markdown renderer loaded without parse function.'));
				}
			};
			script.onerror = () => {
				reject(new Error('Failed to load markdown renderer.'));
			};
			(document.head || document.body || document.documentElement).appendChild(script);
		}).catch((error) => {
			this.markedLoader = null;
			throw error;
		});
		return this.markedLoader;
	};

	/**
	 * Convert markdown into HTML, falling back to a `<pre>` block if needed.
	 * @param {string} markdown
	 * @returns {string}
	 */
	App.prototype.renderMarkdown = function renderMarkdown(markdown) {
		if (globalThis.marked && typeof globalThis.marked.parse === 'function') {
			return globalThis.marked.parse(markdown);
		}
		return `<pre>${escapeHtml(markdown || '')}</pre>`;
	};

	/**
	 * Center the map on an entity's coordinates if available.
	 * @param {'city'|'nation'} kind
	 * @param {object} entity
	 */
	App.prototype.panMapToEntity = function panMapToEntity(kind, entity) {
		if (!this.map || !entity) {
			return;
		}
		const lat = typeof entity.lat === 'number' ? entity.lat : null;
		const lon = typeof entity.lon === 'number' ? entity.lon : null;
		if (lat === null || lon === null) {
			return;
		}
		let targetZoom = this.map.getZoom();
		if (kind === 'city' && targetZoom < this.cityZoomThreshold) {
			targetZoom = this.cityZoomThreshold;
		} else if (kind === 'nation' && targetZoom < 3) {
			targetZoom = 3;
		}
		this.map.setView([lat, lon], targetZoom, { animate: true });
	};

/**
 * React to browser hash changes by loading the requested entity.
 */
App.prototype.onHashChange = function onHashChange() {
		if (this.isInternalAnchorUpdate) {
			return;
		}
		this.applyAnchorFromLocation(false);
	};

/**
 * Interpret the current URL anchor and show the requested entity/lore.
 * @param {boolean} isInitial - Whether this call happens during startup.
 */
App.prototype.applyAnchorFromLocation = function applyAnchorFromLocation(isInitial) {
		if (typeof globalThis.location === 'undefined') {
			return;
		}
		const anchor = this.parseAnchor(globalThis.location.hash);
		if (!anchor) {
			this.lastAnchorValue = null;
			if (!isInitial) {
				this.showPlaceholder();
			}
			return;
		}
		const anchorValue = this.formatAnchor(anchor.kind, anchor.slug);
		this.lastAnchorValue = anchorValue;
		const entity = this.findEntityBySlug(anchor.kind, anchor.slug);
		if (entity) {
			this.suppressAnchorUpdate = true;
			try {
				this.panMapToEntity(anchor.kind, entity);
				this.showEntityInfo(anchor.kind, entity);
			} finally {
				this.suppressAnchorUpdate = false;
			}
			return;
		}
		this.suppressAnchorUpdate = true;
		try {
			this.activatePanel('lore');
			this.expandPanel();
			this.clearHighlight();
			const markdownPath = this.getMarkdownPath(anchor.kind, anchor.slug);
			this.loadMarkdownByPath(anchor.kind, anchor.slug, markdownPath);
		} finally {
			this.suppressAnchorUpdate = false;
		}
	};

/**
 * Build a hash fragment for the given entity kind/slug.
 * @param {'city'|'nation'} kind
 * @param {string} slug
 * @returns {string|null}
 */
App.prototype.formatAnchor = function formatAnchor(kind, slug) {
		if (!kind || !slug) {
			return null;
		}
		const normalizedKind =
			kind === 'city' ? 'city' : kind === 'nation' ? 'nation' : null;
		if (!normalizedKind) {
			return null;
		}
		return `${normalizedKind}:${slug}`;
	};

/**
 * Parse a `kind:slug` hash fragment into a structured object.
 * @param {string} hash
 * @returns {{kind:'city'|'nation',slug:string}|null}
 */
App.prototype.parseAnchor = function parseAnchor(hash) {
		if (typeof hash !== 'string') {
			return null;
		}
		const trimmed = hash.replace(/^#/, '').trim();
		if (!trimmed) {
			return null;
		}
		let separatorIndex = trimmed.indexOf(':');
		if (separatorIndex === -1) {
			separatorIndex = trimmed.indexOf('/');
		}
		if (separatorIndex === -1) {
			return null;
		}
		const kindPart = trimmed.slice(0, separatorIndex).toLowerCase();
		const slugPart = trimmed.slice(separatorIndex + 1);
		if (!slugPart) {
			return null;
		}
		if (kindPart !== 'city' && kindPart !== 'nation') {
			return null;
		}
		return {
			kind: kindPart,
			slug: slugPart
		};
	};

/**
 * Update the browser location hash for the current entity selection.
 * @param {'city'|'nation'} kind
 * @param {string} slug
 */
App.prototype.updateAnchor = function updateAnchor(kind, slug) {
		if (this.suppressAnchorUpdate) {
			return;
		}
		const anchorValue = this.formatAnchor(kind, slug);
		if (!anchorValue) {
			return;
		}
		if (
			this.lastAnchorValue === anchorValue &&
			globalThis.location &&
			globalThis.location.hash === `#${anchorValue}`
		) {
			return;
		}
		this.lastAnchorValue = anchorValue;
		const targetHash = `#${anchorValue}`;
		this.isInternalAnchorUpdate = true;
		try {
			if (globalThis.history && typeof globalThis.history.pushState === 'function') {
				globalThis.history.pushState(null, '', targetHash);
			} else if (globalThis.location) {
				globalThis.location.hash = anchorValue;
			}
		} catch (_error) {
			if (globalThis.location) {
				globalThis.location.hash = anchorValue;
			}
		} finally {
			setTimeout(() => {
				this.isInternalAnchorUpdate = false;
			}, 0);
		}
	};
}
